/*
 * pdp-ui.js — shared UI behaviour for the extended product page. Vanilla JS, no dependencies.
 *
 *   <pdp-accordion-group data-mode="single|multiple">   animated <details> accordions
 *   <pdp-readmore>                                       "Read more / Read less" clamp
 *   a[data-pdp-scroll]                                   smooth scroll to the reviews section
 *
 * Every piece works without JS (native <details>, full text, normal anchor links);
 * this file only adds animation, one-open-at-a-time and the clamp.
 */
(() => {
  if (window.__pdpUI) return;
  window.__pdpUI = true;

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const DURATION = 260;
  const EASING = 'cubic-bezier(.4, 0, .2, 1)';

  /* ------------------------------------------------------------------ */
  /* Accordion group                                                     */
  /* ------------------------------------------------------------------ */
  class PdpAccordionGroup extends HTMLElement {
    connectedCallback() {
      this.onClick = this.onClick.bind(this);
      this.addEventListener('click', this.onClick);

      // In "single" mode only one item may start open.
      if (this.single) {
        const open = this.items.filter((d) => d.open);
        open.slice(1).forEach((d) => (d.open = false));
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    get single() {
      return this.dataset.mode === 'single';
    }

    get items() {
      return Array.from(this.querySelectorAll('details.pdp-acc')).filter((d) => d.closest('pdp-accordion-group') === this);
    }

    onClick(event) {
      const summary = event.target.closest('summary');
      if (!summary || !this.contains(summary)) return;
      const details = summary.parentElement;
      if (!details || !details.matches('details.pdp-acc') || details.closest('pdp-accordion-group') !== this) return;

      event.preventDefault();
      if (details.open && !details.dataset.closing) {
        this.close(details);
      } else {
        this.open(details);
      }
    }

    open(details) {
      if (this.single) {
        this.items.forEach((other) => {
          if (other !== details && other.open) this.close(other);
        });
      }
      this.cancel(details);
      delete details.dataset.closing;

      if (reduceMotion()) {
        details.open = true;
        return;
      }

      const startHeight = details.offsetHeight;
      details.style.overflow = 'hidden';
      details.open = true;
      const endHeight = details.offsetHeight;

      this.animate(details, startHeight, endHeight, () => {
        details.style.height = '';
        details.style.overflow = '';
      });
    }

    close(details) {
      this.cancel(details);

      if (reduceMotion()) {
        details.open = false;
        return;
      }

      const summary = details.querySelector('summary');
      const styles = getComputedStyle(details);
      const chrome = parseFloat(styles.borderTopWidth) + parseFloat(styles.borderBottomWidth);
      const startHeight = details.offsetHeight;
      const endHeight = summary.offsetHeight + chrome;
      details.dataset.closing = 'true';
      details.style.overflow = 'hidden';

      this.animate(details, startHeight, endHeight, () => {
        details.open = false;
        delete details.dataset.closing;
        details.style.height = '';
        details.style.overflow = '';
      });
    }

    animate(details, from, to, done) {
      details._pdpAnim = details.animate(
        { height: [`${from}px`, `${to}px`] },
        { duration: DURATION, easing: EASING }
      );
      const finish = () => {
        details._pdpAnim = null;
        done();
      };
      details._pdpAnim.onfinish = finish;
      details._pdpAnim.oncancel = () => {
        details._pdpAnim = null;
      };
    }

    cancel(details) {
      if (details._pdpAnim) {
        details._pdpAnim.onfinish = null;
        details._pdpAnim.cancel();
        details._pdpAnim = null;
        details.style.height = '';
        details.style.overflow = '';
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* Read more / Read less                                               */
  /* ------------------------------------------------------------------ */
  class PdpReadmore extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector('.pdp-readmore__content');
      this.toggle = this.querySelector('.pdp-readmore__toggle');
      if (!this.content || !this.toggle) return;

      this.lines = Number(this.dataset.lines) || 4;
      this.query = window.matchMedia(this.hasAttribute('data-clamp-desktop') ? 'all' : '(max-width: 749.98px)');
      this.moreLabel = this.dataset.moreLabel || 'Read more';
      this.lessLabel = this.dataset.lessLabel || 'Read less';

      this.onToggle = this.onToggle.bind(this);
      this.measure = this.measure.bind(this);
      this.toggle.addEventListener('click', this.onToggle);
      this.query.addEventListener('change', this.measure);

      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(this.measure);
        this.observer.observe(this.content);
      }
      this.measure();
    }

    disconnectedCallback() {
      if (!this.toggle) return;
      this.toggle.removeEventListener('click', this.onToggle);
      this.query.removeEventListener('change', this.measure);
      this.observer?.disconnect();
    }

    get expanded() {
      return this.hasAttribute('data-expanded');
    }

    collapsedHeight() {
      const lineHeight = parseFloat(getComputedStyle(this.content).lineHeight) || 24;
      return Math.round(lineHeight * this.lines);
    }

    measure() {
      if (this.animating) return;
      const collapsed = this.collapsedHeight();
      const lineHeight = collapsed / this.lines;

      // Only clamp if it actually saves more than about one line.
      const overflowing = this.query.matches && this.content.scrollHeight > collapsed + lineHeight;

      if (!overflowing) {
        this.removeAttribute('data-clamped');
        this.removeAttribute('data-expanded');
        this.content.style.removeProperty('--pdp-collapsed-h');
        this.toggle.hidden = true;
        return;
      }

      this.content.style.setProperty('--pdp-collapsed-h', `${collapsed}px`);
      this.setAttribute('data-clamped', '');
      this.toggle.hidden = false;
      this.syncToggle();
    }

    syncToggle(expanded = this.expanded) {
      this.toggle.setAttribute('aria-expanded', String(expanded));
      const label = this.toggle.querySelector('[data-label]') || this.toggle;
      label.textContent = expanded ? this.lessLabel : this.moreLabel;
    }

    onToggle() {
      const collapsed = this.collapsedHeight();
      const expanding = !this.expanded;
      const full = this.content.scrollHeight;

      if (reduceMotion()) {
        this.toggleAttribute('data-expanded', expanding);
        this.syncToggle();
        return;
      }

      this.animating = true;
      this.content.style.overflow = 'hidden';
      if (expanding) this.setAttribute('data-expanded', '');

      const animation = this.content.animate(
        { maxHeight: expanding ? [`${collapsed}px`, `${full}px`] : [`${full}px`, `${collapsed}px`] },
        { duration: DURATION, easing: EASING }
      );
      this.syncToggle(expanding);
      animation.onfinish = () => {
        if (!expanding) this.removeAttribute('data-expanded');
        this.content.style.overflow = '';
        this.animating = false;
        this.syncToggle();
      };
    }
  }

  /* ------------------------------------------------------------------ */
  /* Smooth scroll to in-page targets (rating → reviews)                 */
  /* ------------------------------------------------------------------ */
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[data-pdp-scroll]');
    if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.button) return;

    const id = decodeURIComponent((link.hash || '').slice(1));
    const target = id && document.getElementById(id);
    if (!target) return; // no reviews section on this page: let the browser handle it

    event.preventDefault();
    target.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
    if (window.history && history.replaceState) history.replaceState(null, '', `#${id}`);

    const focusTarget = target.querySelector('[data-pdp-focus]') || target;
    if (!focusTarget.hasAttribute('tabindex')) focusTarget.setAttribute('tabindex', '-1');
    focusTarget.focus({ preventScroll: true });
  });

  if (!customElements.get('pdp-accordion-group')) customElements.define('pdp-accordion-group', PdpAccordionGroup);
  if (!customElements.get('pdp-readmore')) customElements.define('pdp-readmore', PdpReadmore);
})();
